import React, { useCallback, useMemo, useState } from "react";
import "../../../css/GlobalStyles.css";
import "../../../css/GlobalTextStyles.css";
import { useSelector } from "react-redux";
import { getAccountState } from "../../../redux/slices/accountSlice";
import LargePopupModalContainer from "../../Containers/LargePopupModalContainer";
import BlackHoverButton from "../../Buttons/BlackHoverButton";
import CustomCheckbox from "../../CustomCheckbox";
import { Colors } from "../../../utils/colors";
import { makeDuplicateEvent } from "../../../utils/makeEvent";
import { useNavigate } from "react-router-dom";
import { Event, EventType, PromoCode } from "@markit/common.types";
import LargePopupModalHeader from "../../Containers/LargePopupModalHeader";
import {
  eventHasRequestTickets,
  isEventRelatedType,
  isEventTicketsPaid,
  isExternalGenericLink,
  isProductSpecificType,
  isProductWithCheckout,
  productItemLabel,
} from "@markit/common.utils";
import { getTotalNumFreePromoTickets } from "../../../utils/eventUtils/eventUtils";
import { getTotalNumUserEventLinkTrackers } from "../../../utils/trackingUtils";
import merge from "lodash.merge";
import { CircularProgress } from "@mui/material";
import useAsyncOnMount from "../../../hooks/useAsyncEffectOnMount";
import { NewProductNavigationId } from "../../../navigation/AppParamList";
import CreateFormAndTrackingLinkModal from "../../CreatorDashboard/FormSubmissions/CreateFormAndTrackingLinkModal";
import { getShareDomain } from "../../../utils/shareLink";

interface CloneProductModalProps {
  event: Event;
  isModalVisible: boolean;
  setIsModalVisible: (isModalVisible: boolean) => void;
}

const CloneProductModal = (props: CloneProductModalProps) => {
  const { event, isModalVisible, setIsModalVisible } = props;
  const { account } = useSelector(getAccountState);
  const { accountData } = account;
  const navigate = useNavigate();
  const [numTrackingLinks, setNumTrackingLinks] = useState(0);
  const [numFreeTickets, setNumFreeTickets] = useState(0);
  const [eventDetailsChecked, setEventDetailsChecked] = useState(true);
  const [trackingLinksChecked, setTrackingLinksChecked] = useState(false);
  const [promoChecked, setPromoChecked] = useState(false);
  const [loading, setLoading] = useState(true);
  const [createFormModalVisible, setCreateFormModalVisible] = useState(false);
  const [duplicatedForm, setDuplicatedForm] = useState<Event | undefined>(
    undefined
  );

  const isFormType = useMemo(
    () => isExternalGenericLink(event.eventType),
    [event.eventType]
  );

  const styles = {
    buttonView: {
      whiteSpace: "nowrap",
      borderRadius: 100,
      paddingBlock: 10,
      paddingInline: 12,
    },
  };

  useAsyncOnMount(async () => {
    if (isFormType) {
      setLoading(false);
      return;
    }
    const [numTrackingLinks, numFreePromoTickets] = await Promise.all([
      getTotalNumUserEventLinkTrackers(accountData.uid, event.id),
      getTotalNumFreePromoTickets(event.id),
    ]);
    setNumTrackingLinks(numTrackingLinks);
    setNumFreeTickets(numFreePromoTickets);
    setLoading(false);
  });

  const disableClone = useMemo(
    () =>
      isFormType
        ? !eventDetailsChecked
        : !eventDetailsChecked && !trackingLinksChecked && !promoChecked,
    [eventDetailsChecked, isFormType, promoChecked, trackingLinksChecked]
  );

  const isProductSpecific = useMemo(
    () => isProductSpecificType(event.eventType),
    [event.eventType]
  );

  const itemLabel = useMemo(
    () => productItemLabel(event.eventType),
    [event.eventType]
  );

  const cloneEvent = useCallback(() => {
    if (isFormType) {
      const newForm = {
        ...makeDuplicateEvent(accountData, event, eventDetailsChecked),
        externalLink: event.externalLink,
        start: "",
        end: "",
      };
      setDuplicatedForm(newForm);
      setCreateFormModalVisible(true);
      return;
    }

    const duplicatedEvent = makeDuplicateEvent(
      accountData,
      event,
      eventDetailsChecked
    );
    const clonedPromoCodes: PromoCode[] = promoChecked
      ? event.promoCodes.map((promoCode) => ({
          ...promoCode,
          usedCount: 0,
        }))
      : [];
    const newEvent: Event = merge({
      ...duplicatedEvent,
      promoCodes: clonedPromoCodes,
    });

    const formattedType = event.eventType.toLowerCase().replace(/\s/g, "-");
    const navigationPath = isEventRelatedType(event.eventType)
      ? NewProductNavigationId.EVENT
      : `/new-product/${formattedType}`;
    navigate(navigationPath, {
      state: {
        duplicatedEvent: newEvent,
        cloneDetails: {
          clonedEventId: event.id,
          trackingLinks: trackingLinksChecked,
          promoCodes: promoChecked,
        },
      },
    });
  }, [
    accountData,
    event,
    eventDetailsChecked,
    isFormType,
    navigate,
    promoChecked,
    trackingLinksChecked,
  ]);

  const onEventDetailsChecked = useCallback(() => {
    if (eventDetailsChecked) {
      setPromoChecked(false);
    }
    setEventDetailsChecked(!eventDetailsChecked);
  }, [eventDetailsChecked]);

  const renderItemRow = useCallback(
    (
      title: string,
      subtext: string,
      isChecked: boolean,
      onPress: () => void,
      disabled?: boolean
    ) => (
      <div
        onClick={onPress}
        className="AlignedRowSpacedSelect"
        style={{
          gap: 24,
          opacity: disabled ? 0.5 : 1,
          pointerEvents: disabled ? "none" : "all",
        }}
      >
        <div className="ColumnNormalSelect" style={{ gap: 7 }}>
          <span className="sectionTitle">{title}</span>
          <span className="bodySubtext">{subtext}</span>
        </div>
        <CustomCheckbox
          checked={isChecked}
          sx={{ padding: 0 }}
          altColor={Colors.BLUE5}
        />
      </div>
    ),
    []
  );

  return (
    <>
      <LargePopupModalContainer
        showModal={isModalVisible}
        headerComp={
          <LargePopupModalHeader
            title={
              isFormType
                ? "Clone Form"
                : `Clone ${
                    event.eventType === EventType.POST
                      ? "Post"
                      : itemLabel.entityCap
                  }`
            }
            backOnPress={() => setIsModalVisible(false)}
            headerRight={
              <BlackHoverButton
                title="Clone"
                onPress={cloneEvent}
                containerStyles={styles.buttonView}
                permanentHover
                disabled={disableClone}
              />
            }
          />
        }
        valueComp={
          loading ? (
            <div className="Centering">
              <CircularProgress style={{ color: Colors.GRAY1 }} size={20} />
            </div>
          ) : (
            <div className="ColumnNormal" style={{ gap: 24 }}>
              <span style={{ fontSize: 20, fontWeight: 500 }}>
                Choose What to Clone:
              </span>
              {isFormType
                ? renderItemRow(
                    "Form Details",
                    "Includes: title, description, cover photo, form questions, redirect link, visibility, and theme",
                    eventDetailsChecked,
                    onEventDetailsChecked
                  )
                : renderItemRow(
                    `${
                      event.eventType === EventType.POST
                        ? "Post"
                        : itemLabel.entityCap
                    } Details`,
                    isProductSpecific
                      ? "Includes: title, description, content, theme, visibility, etc."
                      : "Includes: title, date/time, location, description, cover image, tickets, form questions, guest list + visibility settings, theme, & refund policy",
                    eventDetailsChecked,
                    onEventDetailsChecked
                  )}
              {isFormType ? null : (
                <>
                  {numTrackingLinks > 0
                    ? renderItemRow(
                        "Tracking Links",
                        "Includes: Tracking link names",
                        trackingLinksChecked,
                        () => setTrackingLinksChecked(!trackingLinksChecked)
                      )
                    : null}
                  {(isEventTicketsPaid(event) ||
                    eventHasRequestTickets(event)) &&
                  (event.promoCodes.length > 0 || numFreeTickets > 0)
                    ? renderItemRow(
                        "Promo Codes",
                        "Includes: Promo codes created",
                        promoChecked,
                        () => setPromoChecked(!promoChecked),
                        !eventDetailsChecked
                      )
                    : null}
                  {isProductWithCheckout(event.eventType, event.accessLevel)
                    ? renderItemRow(
                        "Text Automation",
                        "Includes: Coming Soon...",
                        false,
                        () => {},
                        true
                      )
                    : null}
                </>
              )}
            </div>
          )
        }
      />
      {createFormModalVisible && duplicatedForm ? (
        <CreateFormAndTrackingLinkModal
          isVisible={createFormModalVisible}
          setIsVisible={setCreateFormModalVisible}
          eventType={event.eventType}
          existingForm={duplicatedForm}
          isCloning
          onConfirmation={(linkId: string) => {
            setIsModalVisible(false);
            window.location.href = `${getShareDomain()}/e/${linkId}/dashboard`;
          }}
        />
      ) : null}
    </>
  );
};

export default CloneProductModal;
